import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/** Mirrors AdminUser in cleanse_api/api/platform/admin.py. */
export interface AdminOwner {
  id: string;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

/** Mirrors OrgSearchRow. */
export interface OrgSearchRow {
  organization_id: string;
  name: string | null;
  tier: string;
  credits: number;
  appsumo_tier: number | null;
  suspended: boolean;
}

export interface OrgSearchResponse {
  items: OrgSearchRow[];
  /** The user an email search found, even one in no organization. */
  user?: AdminOwner | null;
}

/** Mirrors OrgSearchQuota.
 *
 *  Search allowance is a SEPARATE meter from enrichment credits — these
 *  numbers never belong in `credits`. Staff grants land in the bonus bucket,
 *  which survives the AppSumo 30-day monthly reset.
 *
 *  `meter` is the load-bearing field for the admin UI, NOT `metered`: there
 *  are two different metering mechanisms and a grant extends both.
 *
 *  - `appsumo`   — the lifetime/monthly buckets.
 *  - `preview`   — a free org on the fixed People-search preview counter.
 *                  Metered, just by a different mechanism, so a grant works.
 *  - `unmetered` — a paid plan (or stacked Sumoling) whose plan grants search
 *                  outright. The only case where a grant is genuinely inert. */
export interface OrgSearchQuota {
  meter: 'appsumo' | 'trial' | 'preview' | 'unmetered';
  /** Convenience mirror of `meter !== 'unmetered'`. Prefer `meter` — this
   *  can't distinguish the appsumo and preview meters. */
  metered: boolean;
  /** What the org's own plan still allows, EXCLUDING any staff grant.
   *  `total_remaining` includes it. */
  own_remaining: number;
  bonus_remaining: number;
  bonus_granted: number;
  bonus_used: number;
  total_remaining: number;
  /** AppSumo meter only — both 0 on the preview / unmetered meters. */
  lifetime_remaining: number;
  monthly_remaining: number;
}

/** Mirrors OrgDetailResponse. */
export interface OrgDetail {
  organization_id: string;
  name: string | null;
  tier: string;
  is_legacy_pricing: boolean;
  credits: number;
  seats_paid: number;
  member_count: number;
  appsumo_tier: number | null;
  /** Null on an older API response that predates the search-quota field —
   *  render null-safe rather than assuming the buckets are present. */
  search_quota: OrgSearchQuota | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  next_renewal_date: string | null;
  owner: AdminOwner | null;
  suspended: boolean;
  suspended_at: string | null;
  suspended_reason: string | null;
  suspended_by: string | null;
  created_at: string | null;
  /** Scale trial state. Optional because older API responses predate it. */
  trial_active?: boolean;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  trial_credits_granted?: number | null;
  trial_plan_chosen?: 'free' | 'paid' | null;
  /** Why signup skipped the trial, when it did. */
  trial_fraud_denied?: boolean;
  reduced_credits?: boolean;
  /** Per-seat pricing. Null on legacy/V2 orgs; absent on an older API. */
  pricing_version?: string | null;
  pricing_seats?: number | null;
  /** End of paid v4 time; null on v4 Free. */
  pricing_paid_through?: string | null;
}

/** Mirrors TrialAdminState in cleanse_api/api/billing/billing.py. */
export interface TrialAdminState {
  organization_id: string;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  trial_credits_granted: number | null;
  trial_plan_chosen: 'free' | 'paid' | null;
}

/** Mirrors OrgMemberRow. */
export interface OrgMember {
  user_id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string | null;
}

export interface OrgMembersResponse {
  organization_id: string;
  members: OrgMember[];
}

/** Mirrors EnrichmentOutcomeStats / ProviderOutcomeStat / EndpointUsageRow. */
export interface EnrichmentOutcomeStats {
  total: number;
  passed: number;
  failed: number;
  timed_out: number;
  success_rate: number;
  avg_duration_ms: number | null;
}

export interface ProviderOutcomeStat {
  provider: string;
  total: number;
  passed: number;
}

export interface EndpointUsageRow {
  endpoint: string;
  calls: number;
  credits_spent: number;
  errors: number;
}

/** Mirrors ToolCreditRow — net (settled) credits per tool from the ledger. */
export interface ToolCreditRow {
  tool: string;
  net_credits: number;
}

/** Mirrors FeatureCreditRow — net credits for one product area (agents,
 *  plays, sequences, enrichment, …) with the tools that make it up. */
export interface FeatureCreditRow {
  feature: string;
  net_credits: number;
  tools: ToolCreditRow[];
}

/** Mirrors OrgUsageResponse. */
export interface OrgUsage {
  organization_id: string;
  days: number;
  enrichment: EnrichmentOutcomeStats;
  by_provider: ProviderOutcomeStat[];
  credits_used: number;
  api_total_calls: number;
  api_total_credits_spent: number;
  api_total_errors: number;
  api_endpoints: EndpointUsageRow[];
  /** Net settled credits grouped by tool (accurate); the per-endpoint
   *  credits_spent is the reserved cap (gross). Optional so an older backend
   *  (pre-deploy) doesn't crash the panel. */
  credits_by_tool?: ToolCreditRow[];
  /** The same net spend rolled up by product area. Absent on older servers. */
  credits_by_feature?: FeatureCreditRow[];
}

/** Mirrors OrgSuspendResponse. */
export interface OrgSuspendResponse {
  organization_id: string;
  suspended: boolean;
  suspended_at: string | null;
  suspended_reason: string | null;
  suspended_by: string | null;
}

/** Mirrors CreditLedgerEntryRow. Nullable fields reflect legacy rows written
 *  before amount/entry_type/balance_after were populated — render null-safe. */
export interface CreditLedgerEntry {
  id: string;
  created_at: string | null;
  // Member a row is attributed to; null on org-scoped billing events
  // (grants / refills / clawbacks / opening balance / admin overrides).
  user_id: string | null;
  entry_type: string | null;
  operation_kind: string;
  operation: string;
  amount: number | null;
  credits: number;
  balance_after: number | null;
  lead_list_id: string | null;
  lead_id: string | null;
  meta: Record<string, unknown> | null;
}

/** Mirrors MemberCreditLedgerResponse. */
export interface MemberCreditLedgerResponse {
  organization_id: string;
  user_id: string;
  total: number;
  limit: number;
  offset: number;
  entries: CreditLedgerEntry[];
}

/** Mirrors OrgCreditLedgerResponse — the whole org's ledger (all rows,
 *  including the NULL-user_id billing events the member view omits). */
export interface OrgCreditLedgerResponse {
  organization_id: string;
  total: number;
  limit: number;
  offset: number;
  entries: CreditLedgerEntry[];
}

export const adminOrganizationsApi = createApi({
  reducerPath: 'adminOrganizationsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Org'],
  endpoints: (builder) => ({
    // All staff-only (server gates on @cleanlist.ai → 403 otherwise).
    searchOrganizations: builder.query<
      OrgSearchResponse,
      { q: string; limit?: number; offset?: number }
    >({
      query: ({ q, limit, offset }) => ({
        url: '/management/organizations',
        params: { q, limit, offset },
      }),
    }),
    getOrganization: builder.query<OrgDetail, string>({
      query: (orgId) => `/management/organizations/${orgId}`,
      providesTags: (_r, _e, orgId) => [{ type: 'Org', id: orgId }],
    }),
    getOrganizationMembers: builder.query<OrgMembersResponse, string>({
      query: (orgId) => `/management/organizations/${orgId}/members`,
    }),
    getOrganizationUsage: builder.query<
      OrgUsage,
      { orgId: string; days?: number }
    >({
      query: ({ orgId, days }) => ({
        url: `/management/organizations/${orgId}/usage`,
        params: days ? { days } : {},
      }),
    }),
    suspendOrganization: builder.mutation<
      OrgSuspendResponse,
      { orgId: string; reason?: string }
    >({
      query: ({ orgId, reason }) => ({
        url: `/management/organizations/${orgId}/suspend`,
        method: 'POST',
        body: { reason: reason ?? null },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    unsuspendOrganization: builder.mutation<OrgSuspendResponse, string>({
      query: (orgId) => ({
        url: `/management/organizations/${orgId}/unsuspend`,
        method: 'POST',
      }),
      invalidatesTags: (_r, _e, orgId) => [{ type: 'Org', id: orgId }],
    }),
    // Starts the org's workspace setup again as the user who started it, in
    // the same chat. For a setup that ended without a profile because of us.
    rerunOnboardingSetup: builder.mutation<
      { conversation_id: string; status: string },
      string
    >({
      query: (orgId) => ({
        url: `/management/organizations/${orgId}/onboarding-setup/rerun`,
        method: 'POST',
      }),
    }),
    // Org-scoped credit + tier changes. The /admin endpoints already accept
    // organization_id, so no user search is needed from the org context; the
    // Org tag is invalidated so the Overview balance/tier refresh.
    setOrgCredits: builder.mutation<
      {
        organization_id: string;
        credits: number;
        previous_credits: number;
        mode: string;
      },
      { orgId: string; credits: number; mode: 'add' | 'set' }
    >({
      query: ({ orgId, credits, mode }) => ({
        url: '/management/set-credits',
        method: 'POST',
        body: { organization_id: orgId, credits, mode },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    // Search allowance — NOT the credits balance. Separate meter, separate
    // endpoint, and deliberately absent from credit_ledger on the backend
    // (searches never move credits_count, and the ledger asserts
    // ABS(SUM(amount) - credits_count) < 0.0001). `add` tops up the bonus
    // bucket; `set` makes the remaining bonus exactly `credits`, preserving
    // bonus_used as a lifetime record. Invalidating Org refreshes the
    // quota breakdown the Actions tab reads back.
    setOrgSearchCredits: builder.mutation<
      {
        organization_id: string;
        mode: string;
        previous_remaining: number;
        remaining: number;
        bonus_granted: number;
        bonus_used: number;
        meter: OrgSearchQuota['meter'];
        metered: boolean;
      },
      { orgId: string; credits: number; mode: 'add' | 'set' }
    >({
      query: ({ orgId, credits, mode }) => ({
        url: `/management/organizations/${orgId}/search-credits`,
        method: 'POST',
        body: { credits, mode },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    upgradeOrgTier: builder.mutation<
      {
        organization_id: string;
        tier: string;
        product_id: string;
        credits: number;
      },
      {
        orgId: string;
        tier: string;
        credits?: number;
        pricingVersion?: 'v2' | 'v4';
        /** v4 only: seat count (default: members) and months of paid time. */
        seats?: number;
        months?: number;
      }
    >({
      query: ({ orgId, tier, credits, pricingVersion, seats, months }) => ({
        url: '/management/upgrade-tier',
        method: 'POST',
        body: {
          organization_id: orgId,
          tier,
          credits: credits ?? null,
          pricing_version: pricingVersion ?? 'v2',
          seats: seats ?? null,
          months: months ?? null,
        },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    setOrgSeats: builder.mutation<
      { organization_id: string; seats_paid: number },
      { orgId: string; seats: number }
    >({
      query: ({ orgId, seats }) => ({
        url: `/management/organizations/${orgId}/seats`,
        method: 'POST',
        body: { seats },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    // Scale trial. The server treats a repeated request_id as a no-op.
    startOrgTrial: builder.mutation<
      TrialAdminState,
      { orgId: string; days: number; credits?: number }
    >({
      query: ({ orgId, days, credits }) => ({
        url: `/billing/staff/trial/${orgId}/start`,
        method: 'POST',
        body: {
          days,
          credits: credits ?? null,
          request_id: crypto.randomUUID(),
        },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    extendOrgTrial: builder.mutation<
      TrialAdminState,
      { orgId: string; days: number }
    >({
      query: ({ orgId, days }) => ({
        url: `/billing/staff/trial/${orgId}/extend`,
        method: 'POST',
        body: { days, request_id: crypto.randomUUID() },
      }),
      invalidatesTags: (_r, _e, { orgId }) => [{ type: 'Org', id: orgId }],
    }),
    endOrgTrial: builder.mutation<TrialAdminState, string>({
      query: (orgId) => ({
        url: `/billing/staff/trial/${orgId}/revoke`,
        method: 'POST',
        body: { request_id: crypto.randomUUID() },
      }),
      invalidatesTags: (_r, _e, orgId) => [{ type: 'Org', id: orgId }],
    }),
    // Per-member credit ledger (append-only read → no providesTags). entryTypes
    // is serialized as REPEATED `entry_type` params (entry_type=spend&
    // entry_type=refund) so FastAPI's List[str] parses it; the default RTK
    // serializer would comma-join an array into one value and break the filter.
    getMemberCreditLedger: builder.query<
      MemberCreditLedgerResponse,
      {
        orgId: string;
        userId: string;
        entryTypes?: string[];
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ orgId, userId, entryTypes, limit, offset }) => {
        const sp = new URLSearchParams();
        (entryTypes ?? []).forEach((t) => sp.append('entry_type', t));
        if (limit != null) sp.set('limit', String(limit));
        if (offset != null) sp.set('offset', String(offset));
        const qs = sp.toString();
        return {
          url: `/management/organizations/${orgId}/members/${userId}/credit-ledger${qs ? `?${qs}` : ''}`,
        };
      },
    }),
    // Org-level credit ledger — EVERY row for the org, including the org-scoped
    // billing events (grants/refills/clawbacks/opening balance/admin overrides)
    // the per-member view can't show. Same repeated-param serialization as the
    // member query so FastAPI's List[str] parses entry_type filters.
    getOrgCreditLedger: builder.query<
      OrgCreditLedgerResponse,
      { orgId: string; entryTypes?: string[]; limit?: number; offset?: number }
    >({
      query: ({ orgId, entryTypes, limit, offset }) => {
        const sp = new URLSearchParams();
        (entryTypes ?? []).forEach((t) => sp.append('entry_type', t));
        if (limit != null) sp.set('limit', String(limit));
        if (offset != null) sp.set('offset', String(offset));
        const qs = sp.toString();
        return {
          url: `/management/organizations/${orgId}/credit-ledger${qs ? `?${qs}` : ''}`,
        };
      },
    }),
  }),
});

export const {
  useLazySearchOrganizationsQuery,
  useSearchOrganizationsQuery,
  useGetOrganizationQuery,
  useGetOrganizationMembersQuery,
  useGetOrganizationUsageQuery,
  useSuspendOrganizationMutation,
  useUnsuspendOrganizationMutation,
  useRerunOnboardingSetupMutation,
  useSetOrgCreditsMutation,
  useSetOrgSearchCreditsMutation,
  useUpgradeOrgTierMutation,
  useSetOrgSeatsMutation,
  useStartOrgTrialMutation,
  useExtendOrgTrialMutation,
  useEndOrgTrialMutation,
  useGetMemberCreditLedgerQuery,
  useGetOrgCreditLedgerQuery,
} = adminOrganizationsApi;
